/**
 * RoamerHealthModule.ts — Module #9: the Roamer Health™.
 *
 * A platform module (NOT a core engine): an organizational and
 * informational healthcare companion — health profile, local healthcare
 * directory, emergency services, travel health, medication support,
 * insurance prep, community knowledge, predictive alerts. Module pattern:
 *  • Registers with Roamer Core™ as an EngineAdapter via attachToCore().
 *  • Inbound ONLY via Core bus events + manual entry:
 *    - LocationChanged ({ location: {...} } wrapped, flat fallback) →
 *      currentCity/currentCountry, driving location-aware lookups.
 *    - WeatherUpdated / SafetyUpdated / CommunityUpdated / PlacesUpdated are
 *      fusion metadata-only — refresh signals, never data. Travel-health
 *      readings exist only as recorded TravelHealthReading records with
 *      named sources (future providers sync real data).
 *  • Outbound: the Core event union is fixed — predictive alerts publish as
 *    NotificationSent kind 'healthAlert', ONCE per deterministic alert id
 *    (fired ids persisted), and only when allowAlertNotifications is on.
 *    The payload carries the alert kind + id, NEVER health details.
 *  • NOT a diagnostic tool: every informational surface carries
 *    HEALTH_DISCLAIMER; vaccination guidance is informational only.
 *  • Privacy: health data is highly sensitive. Private by default —
 *    sharingConsent master toggle plus granular per-section toggles all
 *    default false; export returns nothing sensitive without explicit
 *    consent. Encryption is a FUTURE provider hook — until one is
 *    registered, storage is plaintext localStorage and the module never
 *    pretends otherwise (encryptionActive() is honest).
 *  • Community knowledge: Roamer Reports™ keep origin 'community' strictly
 *    separated from 'official'; official entries require a named source.
 *  • Insurance: reference records only — no provider is ever called.
 *  • Persistence nomad:health:v1 with record-by-record sanitization, caps,
 *    deep clones at read boundaries; export/backup/restore + deleteAll;
 *    injectable timestamps; handler errors never break the bus.
 */

import { roamerCore, type CoreEvent, type EngineAdapter } from '@/services/core';
import { storageManager } from '@/services/storage/StorageManager';
import {
  DEFAULT_HEALTH_PRIVACY,
  DEFAULT_HEALTH_PROFILE,
  HEALTH_METRICS,
  PROVIDER_KINDS,
  REPORT_KINDS,
  type EmergencyContact,
  type EmergencyDirectory,
  type HealthAlert,
  type HealthcareProvider,
  type HealthEncryptionProvider,
  type HealthExport,
  type HealthMetric,
  type HealthPrivacySettings,
  type HealthProfile,
  type HealthReportKind,
  type InsuranceClaimRef,
  type InsurancePolicyRef,
  type InsuranceProviderConnector,
  type Medication,
  type ProviderKind,
  type RoamerHealthReport,
  type TravelHealthReading,
} from './models';
import { computeHealthAlerts } from './alerts';

const STORAGE_KEY = 'nomad:health:v1';
const MODULE_ID = 'roamer-health-module';
const MAX_CONTACTS = 20;
const MAX_PROVIDERS = 500;
const MAX_DIRECTORIES = 100;
const MAX_READINGS = 1000;
const MAX_MEDICATIONS = 100;
const MAX_POLICIES = 50;
const MAX_CLAIMS = 200;
const MAX_REPORTS = 500;
const MAX_FIRED_ALERTS = 500;

const CLAIM_STATUSES = ['draft', 'submitted', 'approved', 'denied', 'paid'] as const;

function asString(v: unknown): string | null {
  return typeof v === 'string' && v.trim().length > 0 ? v.trim() : null;
}
function asNumberOrNull(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}
function asStringArray(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((s): s is string => typeof s === 'string') : [];
}

interface PersistedState {
  profile: HealthProfile;
  privacy: HealthPrivacySettings;
  currentCity: string | null;
  currentCountry: string | null;
  contacts: EmergencyContact[];
  providers: HealthcareProvider[];
  directories: EmergencyDirectory[];
  readings: TravelHealthReading[];
  medications: Medication[];
  policies: InsurancePolicyRef[];
  claims: InsuranceClaimRef[];
  reports: RoamerHealthReport[];
  firedAlertIds: string[];
  refreshSignalCount: number;
}

export type NewProviderInput = Omit<HealthcareProvider, 'id' | 'addedAt'>;
export type NewReadingInput = Omit<TravelHealthReading, 'id' | 'recordedAt'>;
export type NewMedicationInput = Omit<Medication, 'id' | 'addedAt'>;
export type NewPolicyInput = Omit<InsurancePolicyRef, 'id' | 'addedAt'>;
export type NewReportInput = Omit<RoamerHealthReport, 'id' | 'recordedAt'>;

export class RoamerHealthModule {
  private profile: HealthProfile = { ...DEFAULT_HEALTH_PROFILE };
  private privacy: HealthPrivacySettings = { ...DEFAULT_HEALTH_PRIVACY };
  private currentCity: string | null = null;
  private currentCountry: string | null = null;
  private contacts: EmergencyContact[] = [];
  private providers: HealthcareProvider[] = [];
  private directories: EmergencyDirectory[] = [];
  private readings: TravelHealthReading[] = [];
  private medications: Medication[] = [];
  private policies: InsurancePolicyRef[] = [];
  private claims: InsuranceClaimRef[] = [];
  private reports: RoamerHealthReport[] = [];
  private firedAlertIds: string[] = [];
  private refreshSignalCount = 0;
  private encryptionProvider: HealthEncryptionProvider | null = null;
  private insuranceConnectors: InsuranceProviderConnector[] = [];
  private unsubscribes: (() => void)[] = [];
  private core: typeof roamerCore | null = null;
  private listeners = new Set<() => void>();

  constructor(now: number = Date.now()) {
    this.load(now);
  }

  // ── Core integration ────────────────────────────────────────────────────────

  buildAdapter(): EngineAdapter {
    return {
      id: MODULE_ID,
      name: 'Roamer Health™',
      version: '1.0.0',
      capabilities: ['health-profile', 'healthcare-directory', 'emergency-services', 'travel-health', 'medications', 'health-alerts'],
      healthCheck: async () => true,
    };
  }

  attachToCore(core: typeof roamerCore = roamerCore, now: number = Date.now()): void {
    this.detachFromCore();
    core.registerEngine(this.buildAdapter(), now);
    const safe = (handler: (e: CoreEvent) => void) => (e: CoreEvent) => {
      try {
        handler(e);
      } catch {
        /* a bad payload must never break the bus */
      }
    };
    this.unsubscribes.push(core.on('LocationChanged', safe((e) => this.onLocationChanged(e)), 'module:health'));
    // Fusion-bridged events carry metadata only — refresh signals, never data.
    for (const name of ['WeatherUpdated', 'SafetyUpdated', 'CommunityUpdated', 'PlacesUpdated'] as const) {
      this.unsubscribes.push(core.on(name, safe(() => this.onRefreshSignal()), 'module:health'));
    }
    this.core = core;
  }

  detachFromCore(): void {
    for (const u of this.unsubscribes) u();
    this.unsubscribes = [];
    this.core = null;
  }

  private onLocationChanged(e: CoreEvent): void {
    const raw = (e.payload ?? {}) as Record<string, unknown>;
    const p = (raw.location != null && typeof raw.location === 'object'
      ? raw.location
      : raw) as Record<string, unknown>;
    const city = asString(p.city);
    const country = asString(p.country);
    if (city === this.currentCity && country === this.currentCountry) return;
    if (city != null) this.currentCity = city;
    if (country != null) this.currentCountry = country;
    this.afterUpdate();
  }

  private onRefreshSignal(): void {
    this.refreshSignalCount += 1;
    this.afterUpdate();
  }

  getRefreshSignalCount(): number {
    return this.refreshSignalCount;
  }

  /** Read-only: whether the module is currently attached to Roamer Core. */
  isAttached(): boolean {
    return this.core != null;
  }

  getCurrentLocation(): { city: string | null; country: string | null } {
    return { city: this.currentCity, country: this.currentCountry };
  }

  // ── Health profile & emergency contacts ─────────────────────────────────────

  getProfile(): HealthProfile {
    return structuredClone(this.profile);
  }

  updateProfile(patch: Partial<HealthProfile>): HealthProfile {
    this.profile = { ...this.profile, ...patch };
    this.afterUpdate();
    return this.getProfile();
  }

  addEmergencyContact(name: string, phone: string, relationship: string | null = null, at: number = Date.now()): EmergencyContact | null {
    if (!asString(name) || !asString(phone)) return null;
    const contact: EmergencyContact = {
      id: `contact-${at}-${this.contacts.length}`,
      name: name.trim(),
      relationship: asString(relationship),
      phone: phone.trim(),
      addedAt: at,
    };
    this.contacts.push(contact);
    if (this.contacts.length > MAX_CONTACTS) this.contacts = this.contacts.slice(-MAX_CONTACTS);
    this.afterUpdate();
    return structuredClone(contact);
  }

  removeEmergencyContact(id: string): boolean {
    const before = this.contacts.length;
    this.contacts = this.contacts.filter((c) => c.id !== id);
    if (this.contacts.length === before) return false;
    this.afterUpdate();
    return true;
  }

  getEmergencyContacts(): EmergencyContact[] {
    return structuredClone(this.contacts);
  }

  // ── Local healthcare directory ──────────────────────────────────────────────

  addProvider(input: NewProviderInput, at: number = Date.now()): HealthcareProvider | null {
    if (!asString(input.name) || !asString(input.city)) return null;
    if (!PROVIDER_KINDS.includes(input.kind)) return null;
    const provider: HealthcareProvider = {
      ...input,
      name: input.name.trim(),
      city: input.city.trim(),
      country: asString(input.country),
      address: asString(input.address),
      phone: asString(input.phone),
      englishSpeaking: typeof input.englishSpeaking === 'boolean' ? input.englishSpeaking : null,
      specialty: asString(input.specialty),
      notes: asString(input.notes),
      id: `provider-${at}-${this.providers.length}`,
      addedAt: at,
    };
    this.providers.push(provider);
    if (this.providers.length > MAX_PROVIDERS) this.providers = this.providers.slice(-MAX_PROVIDERS);
    this.afterUpdate();
    return structuredClone(provider);
  }

  /**
   * Find recorded providers. englishSpeakingOnly matches TRUE only —
   * unknown (null) never passes a demanded filter.
   */
  findProviders(filter?: { kind?: ProviderKind; city?: string; englishSpeakingOnly?: boolean }): HealthcareProvider[] {
    let list = this.providers;
    if (filter?.kind) list = list.filter((p) => p.kind === filter.kind);
    if (filter?.city) list = list.filter((p) => p.city.toLowerCase() === filter.city!.toLowerCase());
    if (filter?.englishSpeakingOnly) list = list.filter((p) => p.englishSpeaking === true);
    return structuredClone(list);
  }

  /** Pharmacy finder — recorded pharmacies in the current (or given) city. */
  findPharmacies(city?: string, open24h: boolean = false): HealthcareProvider[] {
    const target = city ?? this.currentCity;
    if (target == null) return [];
    return this.findProviders({ city: target }).filter(
      (p) => p.kind === (open24h ? 'pharmacy24h' : 'pharmacy') || (!open24h && p.kind === 'pharmacy24h'),
    );
  }

  // ── Emergency services ──────────────────────────────────────────────────────

  upsertEmergencyDirectory(directory: Omit<EmergencyDirectory, 'recordedAt'>, at: number = Date.now()): EmergencyDirectory | null {
    const country = asString(directory.country);
    if (!country) return null;
    const record: EmergencyDirectory = {
      ...structuredClone(directory),
      country,
      recordedAt: at,
    };
    this.directories = this.directories.filter((d) => d.country.toLowerCase() !== country.toLowerCase());
    this.directories.push(record);
    if (this.directories.length > MAX_DIRECTORIES) this.directories = this.directories.slice(-MAX_DIRECTORIES);
    this.afterUpdate();
    return structuredClone(record);
  }

  /** Quick access for the current (or given) country. null when nothing recorded. */
  getEmergencyDirectory(country?: string): EmergencyDirectory | null {
    const target = country ?? this.currentCountry;
    if (target == null) return null;
    const found = this.directories.find((d) => d.country.toLowerCase() === target.toLowerCase());
    return found ? structuredClone(found) : null;
  }

  // ── Travel health (recorded readings only) ──────────────────────────────────

  recordReading(input: NewReadingInput, at: number = Date.now()): TravelHealthReading | null {
    if (!HEALTH_METRICS.includes(input.metric) || !asString(input.city) || !asString(input.source)) return null;
    if (input.value == null && asString(input.note) == null) return null; // an empty reading says nothing
    const reading: TravelHealthReading = {
      metric: input.metric,
      city: input.city.trim(),
      value: asNumberOrNull(input.value),
      note: asString(input.note),
      source: input.source.trim(),
      id: `reading-${at}-${this.readings.length}`,
      recordedAt: at,
    };
    this.readings.push(reading);
    if (this.readings.length > MAX_READINGS) this.readings = this.readings.slice(-MAX_READINGS);
    this.afterUpdate();
    return structuredClone(reading);
  }

  /** Location-aware snapshot: latest recorded reading per metric for the current city. */
  getTravelHealthSnapshot(city?: string): Partial<Record<HealthMetric, TravelHealthReading>> {
    const target = city ?? this.currentCity;
    const snapshot: Partial<Record<HealthMetric, TravelHealthReading>> = {};
    if (target == null) return snapshot;
    for (const metric of HEALTH_METRICS) {
      const candidates = this.readings.filter(
        (r) => r.metric === metric && r.city.toLowerCase() === target.toLowerCase(),
      );
      if (candidates.length > 0) {
        snapshot[metric] = structuredClone(candidates.reduce((a, b) => (b.recordedAt > a.recordedAt ? b : a)));
      }
    }
    return snapshot;
  }

  // ── Medication support ──────────────────────────────────────────────────────

  addMedication(input: NewMedicationInput, at: number = Date.now()): Medication | null {
    if (!asString(input.name)) return null;
    const medication: Medication = {
      name: input.name.trim(),
      dosage: asString(input.dosage),
      refillDueAt: asNumberOrNull(input.refillDueAt),
      translations: (input.translations ?? []).filter(
        (t) => asString(t?.language) != null && asString(t?.translatedName) != null,
      ),
      equivalents: (input.equivalents ?? []).filter(
        (e) => asString(e?.country) != null && asString(e?.equivalentName) != null && asString(e?.source) != null,
      ),
      notes: asString(input.notes),
      id: `med-${at}-${this.medications.length}`,
      addedAt: at,
    };
    this.medications.push(medication);
    if (this.medications.length > MAX_MEDICATIONS) this.medications = this.medications.slice(-MAX_MEDICATIONS);
    this.afterUpdate();
    return structuredClone(medication);
  }

  updateMedication(id: string, patch: Partial<Pick<Medication, 'dosage' | 'refillDueAt' | 'notes' | 'translations' | 'equivalents'>>): boolean {
    const medication = this.medications.find((m) => m.id === id);
    if (!medication) return false;
    // Sanitize updates with the same rules as create — unsourced
    // equivalents / malformed translations can never sneak in via patch.
    const safe: Partial<Medication> = {};
    if ('dosage' in patch) safe.dosage = asString(patch.dosage);
    if ('refillDueAt' in patch) safe.refillDueAt = asNumberOrNull(patch.refillDueAt);
    if ('notes' in patch) safe.notes = asString(patch.notes);
    if ('translations' in patch) {
      safe.translations = (patch.translations ?? []).filter(
        (t) => asString(t?.language) != null && asString(t?.translatedName) != null,
      );
    }
    if ('equivalents' in patch) {
      safe.equivalents = (patch.equivalents ?? []).filter(
        (e) => asString(e?.country) != null && asString(e?.equivalentName) != null && asString(e?.source) != null,
      );
    }
    Object.assign(medication, structuredClone(safe));
    this.afterUpdate();
    return true;
  }

  removeMedication(id: string): boolean {
    const before = this.medications.length;
    this.medications = this.medications.filter((m) => m.id !== id);
    if (this.medications.length === before) return false;
    this.afterUpdate();
    return true;
  }

  getMedications(): Medication[] {
    return structuredClone(this.medications);
  }

  // ── Insurance (references only — never calls a provider) ───────────────────

  addPolicy(input: NewPolicyInput, at: number = Date.now()): InsurancePolicyRef | null {
    if (!asString(input.provider) || (input.kind !== 'travel' && input.kind !== 'health')) return null;
    const policy: InsurancePolicyRef = {
      ...input,
      provider: input.provider.trim(),
      policyNumber: asString(input.policyNumber),
      expiresAt: asNumberOrNull(input.expiresAt),
      emergencyAssistanceNumber: asString(input.emergencyAssistanceNumber),
      notes: asString(input.notes),
      id: `policy-${at}-${this.policies.length}`,
      addedAt: at,
    };
    this.policies.push(policy);
    if (this.policies.length > MAX_POLICIES) this.policies = this.policies.slice(-MAX_POLICIES);
    this.afterUpdate();
    return structuredClone(policy);
  }

  getPolicies(): InsurancePolicyRef[] {
    return structuredClone(this.policies);
  }

  openClaim(policyId: string, description: string, at: number = Date.now()): InsuranceClaimRef | null {
    if (!this.policies.some((p) => p.id === policyId) || !asString(description)) return null;
    const claim: InsuranceClaimRef = {
      id: `claim-${at}-${this.claims.length}`,
      policyId,
      description: description.trim(),
      status: 'draft',
      openedAt: at,
    };
    this.claims.push(claim);
    if (this.claims.length > MAX_CLAIMS) this.claims = this.claims.slice(-MAX_CLAIMS);
    this.afterUpdate();
    return structuredClone(claim);
  }

  updateClaimStatus(id: string, status: InsuranceClaimRef['status']): boolean {
    const claim = this.claims.find((c) => c.id === id);
    if (!claim || !CLAIM_STATUSES.includes(status)) return false;
    claim.status = status;
    this.afterUpdate();
    return true;
  }

  getClaims(): InsuranceClaimRef[] {
    return structuredClone(this.claims);
  }

  /** FUTURE provider integrations — registry only, nothing is ever called. */
  registerInsuranceConnector(connector: InsuranceProviderConnector): void {
    if (!this.insuranceConnectors.some((c) => c.id === connector.id)) {
      this.insuranceConnectors.push(connector);
    }
  }

  getInsuranceConnectors(): InsuranceProviderConnector[] {
    return [...this.insuranceConnectors];
  }

  // ── Community knowledge (Roamer Reports™) ───────────────────────────────────

  addReport(input: NewReportInput, at: number = Date.now()): RoamerHealthReport | null {
    if (!REPORT_KINDS.includes(input.kind) || !asString(input.city) || !asString(input.text)) return null;
    if (input.origin !== 'community' && input.origin !== 'official') return null;
    // Official information requires a named source — never anonymous.
    if (input.origin === 'official' && !asString(input.source)) return null;
    const report: RoamerHealthReport = {
      ...input,
      city: input.city.trim(),
      text: input.text.trim(),
      verified: input.verified === true,
      source: asString(input.source),
      id: `report-${at}-${this.reports.length}`,
      recordedAt: at,
    };
    this.reports.push(report);
    if (this.reports.length > MAX_REPORTS) this.reports = this.reports.slice(-MAX_REPORTS);
    this.afterUpdate();
    return structuredClone(report);
  }

  /** Verified experiences stay separated from official information. */
  getReports(filter?: { kind?: HealthReportKind; city?: string; origin?: 'community' | 'official'; verifiedOnly?: boolean }): RoamerHealthReport[] {
    let list = this.reports;
    if (filter?.kind) list = list.filter((r) => r.kind === filter.kind);
    if (filter?.city) list = list.filter((r) => r.city.toLowerCase() === filter.city!.toLowerCase());
    if (filter?.origin) list = list.filter((r) => r.origin === filter.origin);
    if (filter?.verifiedOnly) list = list.filter((r) => r.verified);
    return structuredClone(list);
  }

  // ── Predictive alerts ───────────────────────────────────────────────────────

  /**
   * Rule-based alerts over recorded data. Publishing to the bus happens
   * once per deterministic alert id and only with allowAlertNotifications;
   * the payload carries kind + id, never health details.
   */
  getAlerts(now: number = Date.now()): HealthAlert[] {
    const alerts = computeHealthAlerts({
      currentCity: this.currentCity,
      readings: this.readings,
      medications: this.medications,
      policies: this.policies,
      now,
    });
    let firedNew = false;
    for (const alert of alerts) {
      // firedAlertIds tracks PUBLISHED alerts only: evaluating while
      // notifications are off must not burn the id — enabling consent later
      // still publishes the (still-active) alert exactly once.
      if (!this.privacy.allowAlertNotifications) continue;
      if (this.firedAlertIds.includes(alert.id)) continue;
      this.firedAlertIds.push(alert.id);
      firedNew = true;
      try {
        this.core?.publish('NotificationSent', MODULE_ID, {
          kind: 'healthAlert',
          alertKind: alert.kind,
          alertId: alert.id,
        }, now);
      } catch {
        /* best-effort */
      }
    }
    if (this.firedAlertIds.length > MAX_FIRED_ALERTS) {
      this.firedAlertIds = this.firedAlertIds.slice(-MAX_FIRED_ALERTS);
    }
    if (firedNew) this.afterUpdate();
    return structuredClone(alerts);
  }

  // ── Encryption (FUTURE hook — honest about plaintext) ───────────────────────

  registerEncryptionProvider(provider: HealthEncryptionProvider): void {
    this.encryptionProvider = provider;
    this.persist();
  }

  /** True only when a real provider is registered — never pretends. */
  encryptionActive(): boolean {
    return this.encryptionProvider != null;
  }

  // ── Privacy, export, backup, delete ─────────────────────────────────────────

  getPrivacySettings(): HealthPrivacySettings {
    return { ...this.privacy };
  }

  updatePrivacySettings(patch: Partial<HealthPrivacySettings>): HealthPrivacySettings {
    this.privacy = { ...this.privacy, ...patch };
    this.afterUpdate();
    return this.getPrivacySettings();
  }

  /**
   * Consent-gated export: without sharingConsent every sensitive section is
   * empty; granular toggles gate each section. Non-sensitive reference data
   * (providers, directories, readings) exports freely — it contains no
   * personal health information.
   */
  exportHealth(now: number = Date.now()): HealthExport {
    const consent = this.privacy.sharingConsent;
    return structuredClone({
      exportedAt: now,
      disclaimer: 'Informational only — not medical advice. Always consult a licensed healthcare professional.' as const,
      profile: consent && this.privacy.shareProfile ? this.profile : null,
      emergencyContacts: consent && this.privacy.shareEmergencyContacts ? this.contacts : [],
      medications: consent && this.privacy.shareMedications ? this.medications : [],
      policies: consent && this.privacy.shareInsurance ? this.policies : [],
      claims: consent && this.privacy.shareInsurance ? this.claims : [],
      reports: consent && this.privacy.shareReports ? this.reports : [],
      providers: this.providers,
      directories: this.directories,
      readings: this.readings,
    });
  }

  /** Full personal backup for the user themself — NOT consent-gated sharing. */
  createBackup(): string {
    const state = this.serialize();
    return this.encryptionProvider
      ? this.encryptionProvider.encrypt(JSON.stringify(state))
      : JSON.stringify(state);
  }

  restoreBackup(backup: string, now: number = Date.now()): boolean {
    try {
      const raw = this.encryptionProvider ? this.encryptionProvider.decrypt(backup) : backup;
      JSON.parse(raw); // must at least be valid JSON
      if (typeof localStorage !== 'undefined') localStorage.setItem(STORAGE_KEY, raw);
      this.load(now);
      this.emitChange();
      return true;
    } catch {
      return false;
    }
  }

  deleteAll(): void {
    this.profile = { ...DEFAULT_HEALTH_PROFILE };
    this.privacy = { ...DEFAULT_HEALTH_PRIVACY };
    this.currentCity = null;
    this.currentCountry = null;
    this.contacts = [];
    this.providers = [];
    this.directories = [];
    this.readings = [];
    this.medications = [];
    this.policies = [];
    this.claims = [];
    this.reports = [];
    this.firedAlertIds = [];
    this.refreshSignalCount = 0;
    this.persist();
    this.emitChange();
  }

  onChange(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emitChange(): void {
    for (const l of this.listeners) {
      try {
        l();
      } catch { /* isolated */ }
    }
  }

  private afterUpdate(): void {
    this.persist();
    this.emitChange();
  }

  // ── Persistence ─────────────────────────────────────────────────────────────

  private serialize(): PersistedState {
    return {
      profile: this.profile,
      privacy: this.privacy,
      currentCity: this.currentCity,
      currentCountry: this.currentCountry,
      contacts: this.contacts,
      providers: this.providers,
      directories: this.directories,
      readings: this.readings,
      medications: this.medications,
      policies: this.policies,
      claims: this.claims,
      reports: this.reports,
      firedAlertIds: this.firedAlertIds,
      refreshSignalCount: this.refreshSignalCount,
    };
  }

  private persist(): void {
    if (typeof localStorage === 'undefined') return;
    try {
      const serialized = this.serialize();
      localStorage.setItem(STORAGE_KEY, JSON.stringify(serialized));
      // Cloud sync: gated on explicit operator sharingConsent (master toggle).
      // Health data is highly sensitive — never pushed to cloud without consent.
      // The privacy model is preserved: sharingConsent=false keeps data local-only.
      if (this.privacy.sharingConsent) {
        storageManager.push('health', 'state', serialized);
      }
    } catch {
      /* storage unavailable — keep working in memory */
    }
  }

  /** Called by hydrateHealthFromCloud when a newer cloud copy is available. */
  reloadFromStorage(now = Date.now()): void {
    this.load(now);
    this.emitChange();
  }

  private load(_now: number): void {
    if (typeof localStorage === 'undefined') return;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const state = JSON.parse(raw) as Record<string, unknown> | null;
      if (state == null || typeof state !== 'object') return;

      const pr = (state.profile ?? {}) as Record<string, unknown>;
      this.profile = {
        bloodType: asString(pr.bloodType),
        allergies: asStringArray(pr.allergies),
        preferredLanguage: asString(pr.preferredLanguage),
        travelInsuranceProvider: asString(pr.travelInsuranceProvider),
        insurancePolicyNumber: asString(pr.insurancePolicyNumber),
        primaryPhysician: asString(pr.primaryPhysician),
        medicalNotes: asString(pr.medicalNotes),
      };

      const pv = (state.privacy ?? {}) as Record<string, unknown>;
      this.privacy = {
        sharingConsent: pv.sharingConsent === true,
        shareProfile: pv.shareProfile === true,
        shareEmergencyContacts: pv.shareEmergencyContacts === true,
        shareMedications: pv.shareMedications === true,
        shareInsurance: pv.shareInsurance === true,
        shareReports: pv.shareReports === true,
        allowAlertNotifications: pv.allowAlertNotifications === true,
      };

      this.currentCity = asString(state.currentCity);
      this.currentCountry = asString(state.currentCountry);

      this.contacts = (Array.isArray(state.contacts) ? state.contacts : [])
        .filter((c): c is Record<string, unknown> =>
          c != null && typeof c === 'object' &&
          asString((c as Record<string, unknown>).id) != null &&
          asString((c as Record<string, unknown>).name) != null &&
          asString((c as Record<string, unknown>).phone) != null)
        .map((c): EmergencyContact => ({
          id: c.id as string,
          name: (c.name as string).trim(),
          relationship: asString(c.relationship),
          phone: (c.phone as string).trim(),
          addedAt: asNumberOrNull(c.addedAt) ?? 0,
        }))
        .slice(-MAX_CONTACTS);

      this.providers = (Array.isArray(state.providers) ? state.providers : [])
        .filter((p): p is Record<string, unknown> =>
          p != null && typeof p === 'object' &&
          asString((p as Record<string, unknown>).id) != null &&
          asString((p as Record<string, unknown>).name) != null &&
          asString((p as Record<string, unknown>).city) != null &&
          PROVIDER_KINDS.includes((p as { kind?: never }).kind as never))
        .map((p): HealthcareProvider => ({
          id: p.id as string,
          kind: p.kind as ProviderKind,
          name: (p.name as string).trim(),
          city: (p.city as string).trim(),
          country: asString(p.country),
          address: asString(p.address),
          phone: asString(p.phone),
          englishSpeaking: typeof p.englishSpeaking === 'boolean' ? p.englishSpeaking : null,
          specialty: asString(p.specialty),
          notes: asString(p.notes),
          addedAt: asNumberOrNull(p.addedAt) ?? 0,
        }))
        .slice(-MAX_PROVIDERS);

      this.directories = (Array.isArray(state.directories) ? state.directories : [])
        .filter((d): d is Record<string, unknown> =>
          d != null && typeof d === 'object' &&
          asString((d as Record<string, unknown>).country) != null)
        .map((d): EmergencyDirectory => ({
          country: (d.country as string).trim(),
          general: asString(d.general),
          police: asString(d.police),
          fire: asString(d.fire),
          ambulance: asString(d.ambulance),
          touristPolice: asString(d.touristPolice),
          poisonControl: asString(d.poisonControl),
          embassies: (Array.isArray(d.embassies) ? d.embassies : [])
            .filter((e: unknown): e is Record<string, unknown> => e != null && typeof e === 'object' && asString((e as Record<string, unknown>).name) != null)
            .map((e) => ({ name: (e.name as string).trim(), phone: asString(e.phone), address: asString(e.address) })),
          consulates: (Array.isArray(d.consulates) ? d.consulates : [])
            .filter((e: unknown): e is Record<string, unknown> => e != null && typeof e === 'object' && asString((e as Record<string, unknown>).name) != null)
            .map((e) => ({ name: (e.name as string).trim(), phone: asString(e.phone), address: asString(e.address) })),
          phrases: (Array.isArray(d.phrases) ? d.phrases : [])
            .filter((p: unknown): p is Record<string, unknown> =>
              p != null && typeof p === 'object' &&
              asString((p as Record<string, unknown>).phrase) != null &&
              asString((p as Record<string, unknown>).translation) != null &&
              asString((p as Record<string, unknown>).language) != null)
            .map((p) => ({ phrase: p.phrase as string, translation: p.translation as string, language: p.language as string })),
          recordedAt: asNumberOrNull(d.recordedAt) ?? 0,
        }))
        .slice(-MAX_DIRECTORIES);

      this.readings = (Array.isArray(state.readings) ? state.readings : [])
        .filter((r): r is Record<string, unknown> =>
          r != null && typeof r === 'object' &&
          asString((r as Record<string, unknown>).id) != null &&
          asString((r as Record<string, unknown>).city) != null &&
          asString((r as Record<string, unknown>).source) != null &&
          HEALTH_METRICS.includes((r as { metric?: never }).metric as never) &&
          (asNumberOrNull((r as Record<string, unknown>).value) != null ||
            asString((r as Record<string, unknown>).note) != null))
        .map((r): TravelHealthReading => ({
          id: r.id as string,
          metric: r.metric as HealthMetric,
          city: (r.city as string).trim(),
          value: asNumberOrNull(r.value),
          note: asString(r.note),
          source: (r.source as string).trim(),
          recordedAt: asNumberOrNull(r.recordedAt) ?? 0,
        }))
        .slice(-MAX_READINGS);

      this.medications = (Array.isArray(state.medications) ? state.medications : [])
        .filter((m): m is Record<string, unknown> =>
          m != null && typeof m === 'object' &&
          asString((m as Record<string, unknown>).id) != null &&
          asString((m as Record<string, unknown>).name) != null)
        .map((m): Medication => ({
          id: m.id as string,
          name: (m.name as string).trim(),
          dosage: asString(m.dosage),
          refillDueAt: asNumberOrNull(m.refillDueAt),
          translations: (Array.isArray(m.translations) ? m.translations : [])
            .filter((t: unknown): t is { language: string; translatedName: string } =>
              t != null && typeof t === 'object' &&
              asString((t as Record<string, unknown>).language) != null &&
              asString((t as Record<string, unknown>).translatedName) != null),
          equivalents: (Array.isArray(m.equivalents) ? m.equivalents : [])
            .filter((e: unknown): e is { country: string; equivalentName: string; source: string } =>
              e != null && typeof e === 'object' &&
              asString((e as Record<string, unknown>).country) != null &&
              asString((e as Record<string, unknown>).equivalentName) != null &&
              asString((e as Record<string, unknown>).source) != null),
          notes: asString(m.notes),
          addedAt: asNumberOrNull(m.addedAt) ?? 0,
        }))
        .slice(-MAX_MEDICATIONS);

      this.policies = (Array.isArray(state.policies) ? state.policies : [])
        .filter((p): p is Record<string, unknown> =>
          p != null && typeof p === 'object' &&
          asString((p as Record<string, unknown>).id) != null &&
          asString((p as Record<string, unknown>).provider) != null &&
          ((p as Record<string, unknown>).kind === 'travel' || (p as Record<string, unknown>).kind === 'health'))
        .map((p): InsurancePolicyRef => ({
          id: p.id as string,
          kind: p.kind as 'travel' | 'health',
          provider: (p.provider as string).trim(),
          policyNumber: asString(p.policyNumber),
          expiresAt: asNumberOrNull(p.expiresAt),
          emergencyAssistanceNumber: asString(p.emergencyAssistanceNumber),
          notes: asString(p.notes),
          addedAt: asNumberOrNull(p.addedAt) ?? 0,
        }))
        .slice(-MAX_POLICIES);

      const policyIds = new Set(this.policies.map((p) => p.id));
      this.claims = (Array.isArray(state.claims) ? state.claims : [])
        .filter((c): c is InsuranceClaimRef =>
          c != null && typeof c === 'object' &&
          asString((c as Record<string, unknown>).id) != null &&
          typeof (c as InsuranceClaimRef).policyId === 'string' &&
          policyIds.has((c as InsuranceClaimRef).policyId) &&
          asString((c as Record<string, unknown>).description) != null &&
          CLAIM_STATUSES.includes((c as InsuranceClaimRef).status))
        .slice(-MAX_CLAIMS);

      this.reports = (Array.isArray(state.reports) ? state.reports : [])
        .filter((r): r is Record<string, unknown> =>
          r != null && typeof r === 'object' &&
          asString((r as Record<string, unknown>).id) != null &&
          asString((r as Record<string, unknown>).city) != null &&
          asString((r as Record<string, unknown>).text) != null &&
          REPORT_KINDS.includes((r as { kind?: never }).kind as never) &&
          ((r as Record<string, unknown>).origin === 'community' ||
            ((r as Record<string, unknown>).origin === 'official' &&
              asString((r as Record<string, unknown>).source) != null)))
        .map((r): RoamerHealthReport => ({
          id: r.id as string,
          kind: r.kind as HealthReportKind,
          city: (r.city as string).trim(),
          text: (r.text as string).trim(),
          origin: r.origin as 'community' | 'official',
          verified: r.verified === true,
          source: asString(r.source),
          recordedAt: asNumberOrNull(r.recordedAt) ?? 0,
        }))
        .slice(-MAX_REPORTS);

      this.firedAlertIds = asStringArray(state.firedAlertIds).slice(-MAX_FIRED_ALERTS);
      const refresh = asNumberOrNull(state.refreshSignalCount);
      this.refreshSignalCount = refresh != null && refresh >= 0 ? Math.floor(refresh) : 0;
    } catch {
      /* corrupt state — start fresh rather than crash */
    }
  }
}

/** App-wide singleton. Call attachToCore() during app bootstrap (after roamerCore.initialize()). */
export const roamerHealthModule = new RoamerHealthModule();

/**
 * Boot hydration: fetches the cloud copy of the Health™ state ONLY when the
 * operator's sharingConsent is currently true (respecting the privacy guarantee
 * that health data never leaves the device without explicit consent).
 * If sharingConsent is false the cloud copy is ignored and local state stands.
 * Fire-and-forget — boot never blocks the network.
 */
export async function hydrateHealthFromCloud(): Promise<boolean> {
  // Respect privacy: only hydrate if operator has consented to cloud sync.
  const current = roamerHealthModule.getPrivacySettings();
  if (!current.sharingConsent) return false;
  const cloud = await storageManager.hydrate<unknown>('health', 'state');
  if (!cloud) return false;
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('nomad:health:v1', JSON.stringify(cloud));
    }
  } catch { return false; }
  roamerHealthModule.reloadFromStorage();
  return true;
}
